import type { Texts } from '~/texts/runtime';

export type SidebarSectionKey =
  | 'core-system'
  | 'control'
  | 'detection'
  | 'video'
  | 'automation'
  | 'installation'
  | 'software';

export type SidebarSection = {
  key: SidebarSectionKey;
  textsKey: keyof typeof Texts;
};

/**
 * Order matches the mockup's section headings, top to bottom.
 * The former "Software" section is intentionally omitted — that content now
 * lives under the standalone "Software and applications" product line instead.
 * Flexi's `1_16`/`1_17` and Mercury's `2_18`/`2_19` legacy pages for this
 * section were removed from the catalog entirely.
 */
export const SIDEBAR_SECTIONS: SidebarSection[] = [
  { key: 'core-system', textsKey: 'SidebarSectionCoreSystem' },
  { key: 'control', textsKey: 'SidebarSectionControl' },
  { key: 'detection', textsKey: 'SidebarSectionDetection' },
  { key: 'video', textsKey: 'SidebarSectionVideo' },
  { key: 'automation', textsKey: 'SidebarSectionAutomation' },
  { key: 'installation', textsKey: 'SidebarSectionInstallation' },
];

/**
 * Flexi (`1_*`) and Mercury (`2_*`) subcategoryPage ids grouped into the 7 sidebar
 * sections, per the spec's Technical Context tables. Mercury's former sirens
 * duplicate (`2_4`) was merged into `2_7` sirens-and-indicators, which lands in
 * `detection`; Mercury's duplicate accessories bucket (`2_5`) was merged into
 * `2_16` installation-accessories.
 */
export const SIDEBAR_SECTION_BY_SUBCATEGORY_ID: Record<string, SidebarSectionKey> = {
  // Flexi
  '1_0': 'core-system',
  '1_1': 'control',
  '1_2': 'control',
  '1_3': 'detection',
  '1_4': 'detection',
  '1_5': 'detection',
  '1_6': 'video',
  '1_7': 'automation',
  '1_8': 'automation',
  '1_9': 'automation',
  '1_10': 'automation',
  '1_11': 'installation',
  '1_12': 'installation',
  '1_13': 'installation',
  '1_14': 'installation',
  '1_15': 'installation',
  // Mercury
  '2_0': 'core-system',
  '2_1': 'control',
  '2_2': 'control',
  '2_3': 'detection',
  '2_6': 'detection',
  '2_7': 'detection',
  '2_8': 'video',
  '2_9': 'automation',
  '2_10': 'automation',
  '2_11': 'automation',
  '2_12': 'automation',
  '2_13': 'installation',
  '2_14': 'installation',
  '2_15': 'installation',
  '2_16': 'installation',
  '2_17': 'installation',
};

const ICON_BASE = '/images/category-sidebar-menu';
const REMOTE_CONTROLS_ICON = '/images/products-mega-menu/item_menu_remote_controls.svg';

const controlPanel = `${ICON_BASE}/side_menu_item_control_panel.svg`;
const keyboard = `${ICON_BASE}/side_menu_item_keyboard.svg`;
const detector = `${ICON_BASE}/side_menu_item_detector.svg`;
const panicButton = `${ICON_BASE}/side_menu_item_panic_button.svg`;
const sirens = `${ICON_BASE}/side_menu_item_sirens.svg`;
const ipCamera = `${ICON_BASE}/side_menu_item_ip_camera.svg`;
const moduleRelay = `${ICON_BASE}/side_menu_item_module_relay.svg`;
const temperature = `${ICON_BASE}/side_menu_item_temperature.svg`;
const shades = `${ICON_BASE}/side_menu_item_shades.svg`;
const meter = `${ICON_BASE}/side_menu_item_meter.svg`;
const cables = `${ICON_BASE}/side_menu_item_cables.svg`;
const wireless = `${ICON_BASE}/side_menu_item_wireless.svg`;
const batteries = `${ICON_BASE}/side_menu_item_batteries.svg`;
const accessories = `${ICON_BASE}/side_menu_item_accessories.svg`;
const source = `${ICON_BASE}/side_menu_item_source.svg`;

/**
 * Icon for each Flexi/Mercury subcategoryPage id. Mercury's legacy items with no
 * exact name match reuse their thematic counterpart's icon: `2_1` keypads →
 * keyboard.
 */
export const SIDEBAR_ICON_BY_SUBCATEGORY_ID: Record<string, string> = {
  // Flexi
  '1_0': controlPanel,
  '1_1': keyboard,
  '1_2': REMOTE_CONTROLS_ICON,
  '1_3': detector,
  '1_4': panicButton,
  '1_5': sirens,
  '1_6': ipCamera,
  '1_7': moduleRelay,
  '1_8': temperature,
  '1_9': shades,
  '1_10': meter,
  '1_11': cables,
  '1_12': wireless,
  '1_13': batteries,
  '1_14': accessories,
  '1_15': source,
  // Mercury
  '2_0': controlPanel,
  '2_1': keyboard,
  '2_2': REMOTE_CONTROLS_ICON,
  '2_3': detector,
  '2_6': panicButton,
  '2_7': sirens,
  '2_8': ipCamera,
  '2_9': moduleRelay,
  '2_10': temperature,
  '2_11': shades,
  '2_12': meter,
  '2_13': cables,
  '2_14': wireless,
  '2_15': batteries,
  '2_16': accessories,
  '2_17': source,
};
